import { FC, useState } from "react"
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material"
import DeleteIcon from "@mui/icons-material/DeleteOutlined"
import {
  AutocompleteElement,
  DatePickerElement,
  FormContainer,
  useForm,
} from "react-hook-form-mui"
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query"
import { format } from "date-fns"
import { formatDate } from "@/core/utils"
import { SnackbarAlert } from "@core/components"
import {
  useAddCourierMutation,
  useDeleteCourierMutation,
  useRealizationsFiltersQuery,
} from "../../store"
import { Realization } from "../../interfaces"

interface IFormData {
  courier: Realization.IdName | null
  issued_date: Date | null
}

const defaultValues: IFormData = {
  courier: null,
  issued_date: new Date(),
}

const isFetchBaseQueryError = (e: unknown): e is FetchBaseQueryError =>
  typeof e === "object" && e !== null && "status" in e

const DUPLICATE_COURIER_STATUSES = [409]

interface IProps {
  batch: Realization.ProductBatch
  canWrite: boolean
}

export const BatchCouriersPanel: FC<IProps> = ({ batch, canWrite }) => {
  // Backend may send `null`/omit the field for batches without couriers
  // instead of an empty array, so guard defensively here.
  const couriers = batch.couriers ?? []

  const { data: filters, isLoading: filtersLoading } =
    useRealizationsFiltersQuery({})
  const [addCourier, { isLoading: isAdding }] = useAddCourierMutation()
  const [deleteCourier] = useDeleteCourierMutation()
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const formContext = useForm<IFormData>({ defaultValues })

  const handleAdd = formContext.handleSubmit(async (values) => {
    if (!values.courier) return
    try {
      await addCourier({
        pathParams: { batch_id: batch.id },
        body: {
          staff_id: values.courier.id,
          issued_date: values.issued_date
            ? format(values.issued_date, "yyyy-MM-dd")
            : undefined,
        },
      }).unwrap()
      formContext.reset(defaultValues)
    } catch (e) {
      const status = isFetchBaseQueryError(e) ? e.status : undefined
      setErrorMsg(
        typeof status === "number" &&
          DUPLICATE_COURIER_STATUSES.includes(status)
          ? "Курьер уже в партии"
          : "Не удалось добавить курьера",
      )
    }
  })

  const handleDelete = async (courierId: number) => {
    setDeletingId(courierId)
    try {
      await deleteCourier({
        pathParams: { batch_id: batch.id, courier_id: courierId },
      }).unwrap()
    } catch {
      setErrorMsg("Не удалось удалить курьера")
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <Box p={2} sx={{ backgroundColor: "background.default" }}>
      <Typography variant="subtitle2" mb={1}>
        Курьеры партии {batch.batch_number}
      </Typography>

      {couriers.length ? (
        <List dense disablePadding>
          {couriers.map((courier) => (
            <ListItem
              key={courier.id}
              disableGutters
              secondaryAction={
                canWrite && (
                  <IconButton
                    edge="end"
                    size="small"
                    disabled={deletingId === courier.id}
                    onClick={() => handleDelete(courier.id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )
              }
            >
              <ListItemText
                primary={courier.staff_name}
                secondary={`Выдано: ${formatDate(courier.issued_date)}`}
              />
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary" mb={1}>
          Курьеры не назначены
        </Typography>
      )}

      {canWrite && (
        <FormContainer formContext={formContext} handleSubmit={handleAdd}>
          <Stack direction="row" spacing={1} alignItems="flex-start" mt={2}>
            <Box minWidth={220}>
              <AutocompleteElement
                name="courier"
                label="Курьер"
                loading={filtersLoading}
                options={filters?.couriers ?? []}
                autocompleteProps={{
                  fullWidth: true,
                  size: "small",
                  getOptionLabel: (o: Realization.IdName) => o.name,
                  isOptionEqualToValue: (o, v) => o.id === v.id,
                }}
              />
            </Box>
            <Box minWidth={180}>
              <DatePickerElement
                name="issued_date"
                label="Дата выдачи"
                inputProps={{ fullWidth: true, size: "small" }}
              />
            </Box>
            <Button
              type="submit"
              variant="outlined"
              disabled={isAdding}
              sx={{ mt: "1px" }}
            >
              Добавить&nbsp;{isAdding && <CircularProgress size={16} />}
            </Button>
          </Stack>
        </FormContainer>
      )}

      <SnackbarAlert
        message={errorMsg ?? ""}
        isOpen={!!errorMsg}
        variant="error"
        snackbarProps={{ onClose: () => setErrorMsg(null) }}
      />
    </Box>
  )
}
